/**
 * Storefront appearance tokens + per-slot presentation.
 * Colors are curated palettes (complementary / analogous) — no free clash pickers.
 */

import type { Language } from '../types';
import { pickI18nText } from './i18n';
import {
  DEFAULT_FOCUS_OUTLINE,
  focusOutlineCssVars,
  normalizeFocusOutline,
} from './focusOutline.js';
import {
  NOTE_COLOR_ROLES,
  noteColorRoleCssVar,
  type NoteColorRole,
} from './noteBlog';
import type {
  StorefrontBindings,
  StorefrontContentWidth,
  StorefrontModuleId,
  StorefrontSlotId,
} from './storefrontComposition';

/** Keep in sync with STOREFRONT_SLOTS / STOREFRONT_BODY_SLOTS (avoid circular import). */
const ALL_SLOTS: StorefrontSlotId[] = [
  'headerBrand',
  'headerNav',
  'headerLang',
  'headerAuth',
  'hero',
  'main1',
  'main2',
  'main3',
  'main4',
  'main5',
  'main6',
  'footer',
];

const BODY_SLOTS: StorefrontSlotId[] = [
  'hero',
  'main1',
  'main2',
  'main3',
  'main4',
  'main5',
  'main6',
  'footer',
];

export const STOREFRONT_PALETTE_IDS = [
  'chalk_ink',
  'mist_navy',
  'forest_fog',
  'sand_espresso',
  'ocean_slate',
  'rose_stone',
  'graphite',
  'lilac_ink',
  'midnight',
  'charcoal_sand',
  'deep_forest',
  'ink_violet',
  'custom',
] as const;

export type StorefrontPaletteId = (typeof STOREFRONT_PALETTE_IDS)[number];

export const STOREFRONT_SEED_MODES = ['light', 'dark'] as const;
export type StorefrontSeedMode = (typeof STOREFRONT_SEED_MODES)[number];

/** SmartUI-style hue schemes from one seed (see smartcolors.js). */
export const STOREFRONT_SEED_HARMONIES = [
  'complementary',
  'analogous',
  'triadic',
  'mono',
] as const;
export type StorefrontSeedHarmony = (typeof STOREFRONT_SEED_HARMONIES)[number];

export const STOREFRONT_PAGE_BG_MODES = ['cover', 'pattern'] as const;
export type StorefrontPageBgMode = (typeof STOREFRONT_PAGE_BG_MODES)[number];

export type StorefrontPageBackground = {
  imageUrl: string | null;
  mode: StorefrontPageBgMode;
  /** Tile size for pattern mode (px). */
  patternSize: number;
  /**
   * 0–100 veil over the photo: 50 = none,
   * below 50 = lighten (white), above 50 = darken (black).
   */
  overlayBalance: number;
};

export const DEFAULT_PAGE_BACKGROUND: StorefrontPageBackground = {
  imageUrl: null,
  mode: 'cover',
  patternSize: 240,
  overlayBalance: 50,
};

export const STOREFRONT_CONTENT_PANELS = ['off', 'soft', 'solid'] as const;
export type StorefrontContentPanel = (typeof STOREFRONT_CONTENT_PANELS)[number];

/** Text/chrome over page photo: follow palette, or force light/dark for contrast. */
export const STOREFRONT_PHOTO_CHROMES = ['palette', 'light', 'dark'] as const;
export type StorefrontPhotoChrome = (typeof STOREFRONT_PHOTO_CHROMES)[number];

/** Extra cart/favorites icons (footer always shows; this is the secondary placement). */
export const STOREFRONT_CART_FAV_PLACEMENTS = [
  'header_brand',
  'header_lang',
  'floating',
] as const;
export type StorefrontCartFavPlacement = (typeof STOREFRONT_CART_FAV_PLACEMENTS)[number];

/** Max veil strength at slider ends (0 or 100). */
export const PAGE_BG_OVERLAY_MAX = 0.8;

/** Build CSS veil layer from overlayBalance; null when neutral (50). */
export function pageBgOverlayVeil(overlayBalance: number): string | null {
  const bal = Math.min(100, Math.max(0, overlayBalance));
  const t = (bal - 50) / 50;
  const strength = Math.abs(t) * PAGE_BG_OVERLAY_MAX;
  if (strength < 0.01) return null;
  const rgb = t > 0 ? '0,0,0' : '255,255,255';
  return `linear-gradient(rgba(${rgb},${strength}), rgba(${rgb},${strength}))`;
}

/**
 * Migrate legacy overlayOpacity (0–80 black-only) → overlayBalance (0–100 centered).
 */
export function normalizeOverlayBalance(raw: Record<string, unknown>): number {
  const balRaw = Number(raw.overlayBalance ?? raw.overlay_balance);
  if (Number.isFinite(balRaw)) {
    return Math.min(100, Math.max(0, Math.round(balRaw)));
  }
  const legacy = Number(raw.overlayOpacity ?? raw.overlay_opacity);
  if (Number.isFinite(legacy)) {
    const clamped = Math.min(80, Math.max(0, legacy));
    return Math.round(50 + (clamped / 80) * 50);
  }
  return DEFAULT_PAGE_BACKGROUND.overlayBalance;
}

export const STOREFRONT_FONT_PAIR_IDS = [
  'system',
  'classic',
  'modern',
  'editorial',
  'heebo',
  'rubik',
  'assistant',
  'frank',
] as const;

export type StorefrontFontPairId = (typeof STOREFRONT_FONT_PAIR_IDS)[number];

export const STOREFRONT_TYPE_SCALES = ['sm', 'md', 'lg'] as const;
export type StorefrontTypeScale = (typeof STOREFRONT_TYPE_SCALES)[number];

export const STOREFRONT_ALIGNS = ['left', 'center'] as const;
export type StorefrontAlign = (typeof STOREFRONT_ALIGNS)[number];

export const STOREFRONT_DENSITIES = ['compact', 'comfortable'] as const;
export type StorefrontDensity = (typeof STOREFRONT_DENSITIES)[number];

export const STOREFRONT_RADII = ['sharp', 'soft', 'round'] as const;
export type StorefrontRadius = (typeof STOREFRONT_RADII)[number];

export const STOREFRONT_SLOT_SCALES = ['sm', 'md', 'lg', 'xl'] as const;
export type StorefrontSlotScale = (typeof STOREFRONT_SLOT_SCALES)[number];

/** Menu / module-heading font size as % of base (100 = default). */
export const STOREFRONT_MENU_FONT_SCALE_MIN = 100;
export const STOREFRONT_MENU_FONT_SCALE_MAX = 250;
export const STOREFRONT_MODULE_HEADING_SCALE_MIN = 100;
export const STOREFRONT_MODULE_HEADING_SCALE_MAX = 500;

/** CSS font-weight for menu / module headings (100–900, step 100). */
export const STOREFRONT_FONT_WEIGHT_MIN = 100;
export const STOREFRONT_FONT_WEIGHT_MAX = 900;
export const STOREFRONT_FONT_WEIGHT_STEP = 100;

export function normalizeAppearancePercentScale(
  raw: unknown,
  fallback: number,
  min: number,
  max: number,
): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

export function normalizeAppearanceFontWeight(raw: unknown, fallback: number): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  const snapped = Math.round(n / STOREFRONT_FONT_WEIGHT_STEP) * STOREFRONT_FONT_WEIGHT_STEP;
  return Math.min(
    STOREFRONT_FONT_WEIGHT_MAX,
    Math.max(STOREFRONT_FONT_WEIGHT_MIN, snapped),
  );
}

export const STOREFRONT_BORDER_WIDTHS = [1, 2, 3] as const;
export type StorefrontBorderWidth = (typeof STOREFRONT_BORDER_WIDTHS)[number];

export type StorefrontPaletteTokens = {
  pageBg: string;
  headerBg: string;
  footerBg: string;
  textPrimary: string;
  textSecondary: string;
  borderColor: string;
  blockBorderColor: string;
};

/** Curated surface stories — tested pairs/triples, accent stays in Theme. */
export const STOREFRONT_PALETTES: Record<StorefrontPaletteId, StorefrontPaletteTokens> = {
  chalk_ink: {
    pageBg: '#ffffff',
    headerBg: '#ffffff',
    footerBg: '#f8fafc',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    borderColor: '#e2e8f0',
    blockBorderColor: '#cbd5e1',
  },
  mist_navy: {
    pageBg: '#f1f5f9',
    headerBg: '#e2e8f0',
    footerBg: '#e2e8f0',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    borderColor: '#cbd5e1',
    blockBorderColor: '#94a3b8',
  },
  forest_fog: {
    pageBg: '#f4f7f4',
    headerBg: '#e8efe9',
    footerBg: '#e8efe9',
    textPrimary: '#14532d',
    textSecondary: '#3f6212',
    borderColor: '#c5d5c8',
    blockBorderColor: '#86a789',
  },
  sand_espresso: {
    pageBg: '#f7f3ee',
    headerBg: '#efe8df',
    footerBg: '#efe8df',
    textPrimary: '#3b2f2f',
    textSecondary: '#6b5b4f',
    borderColor: '#ddd0c2',
    blockBorderColor: '#b89a7a',
  },
  ocean_slate: {
    pageBg: '#f0f4f8',
    headerBg: '#e4ebf2',
    footerBg: '#d9e2ec',
    textPrimary: '#102a43',
    textSecondary: '#486581',
    borderColor: '#bcccdc',
    blockBorderColor: '#627d98',
  },
  rose_stone: {
    pageBg: '#faf6f7',
    headerBg: '#f3eaea',
    footerBg: '#f3eaea',
    textPrimary: '#3f2a2c',
    textSecondary: '#7a5c60',
    borderColor: '#e4d4d6',
    blockBorderColor: '#c9a4a8',
  },
  graphite: {
    pageBg: '#f8fafc',
    headerBg: '#1e293b',
    footerBg: '#0f172a',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    borderColor: '#e2e8f0',
    blockBorderColor: '#94a3b8',
  },
  lilac_ink: {
    pageBg: '#f7f5fb',
    headerBg: '#eeeaf6',
    footerBg: '#eeeaf6',
    textPrimary: '#2e1065',
    textSecondary: '#5b21b6',
    borderColor: '#ddd6fe',
    blockBorderColor: '#a78bfa',
  },
  midnight: {
    pageBg: '#0b1220',
    headerBg: '#111827',
    footerBg: '#020617',
    textPrimary: '#f1f5f9',
    textSecondary: '#94a3b8',
    borderColor: '#1e293b',
    blockBorderColor: '#334155',
  },
  charcoal_sand: {
    pageBg: '#171412',
    headerBg: '#1f1a17',
    footerBg: '#0f0d0c',
    textPrimary: '#f5f0e8',
    textSecondary: '#b8a99a',
    borderColor: '#2e2824',
    blockBorderColor: '#5c4f45',
  },
  deep_forest: {
    pageBg: '#0c1410',
    headerBg: '#122018',
    footerBg: '#08100c',
    textPrimary: '#e8f5ec',
    textSecondary: '#8fbc9c',
    borderColor: '#1a2e22',
    blockBorderColor: '#3d6b4f',
  },
  ink_violet: {
    pageBg: '#120f1a',
    headerBg: '#1a1526',
    footerBg: '#0a0812',
    textPrimary: '#f3eefc',
    textSecondary: '#b6a3d9',
    borderColor: '#2a2140',
    blockBorderColor: '#6b5299',
  },
  /** Placeholder — real tokens come from seed via resolveAppearancePalette. */
  custom: {
    pageBg: '#ffffff',
    headerBg: '#f8fafc',
    footerBg: '#f1f5f9',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    borderColor: '#e2e8f0',
    blockBorderColor: '#94a3b8',
  },
};

export type StorefrontFontPair = {
  heading: string;
  body: string;
  /** Optional Google Fonts family query (injected once). */
  google?: string;
};

export const STOREFRONT_FONT_PAIRS: Record<StorefrontFontPairId, StorefrontFontPair> = {
  system: {
    heading: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    body: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  classic: {
    heading: 'Georgia, "Times New Roman", serif',
    body: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  modern: {
    heading: '"DM Sans", system-ui, sans-serif',
    body: '"Source Sans 3", system-ui, sans-serif',
    // Variable axes so menu/heading weight sliders (100–900) can resolve.
    google: 'family=DM+Sans:wght@100..1000&family=Source+Sans+3:wght@200..900',
  },
  editorial: {
    heading: '"Libre Baskerville", Georgia, serif',
    body: '"Source Sans 3", system-ui, sans-serif',
    google: 'family=Libre+Baskerville:wght@400;700&family=Source+Sans+3:wght@200..900',
  },
  /** Hebrew-first UI sans — closest free spirit to Almoni-style sites. */
  heebo: {
    heading: '"Heebo", system-ui, sans-serif',
    body: '"Heebo", system-ui, sans-serif',
    google: 'family=Heebo:wght@100..900',
  },
  rubik: {
    heading: '"Rubik", system-ui, sans-serif',
    body: '"Rubik", system-ui, sans-serif',
    google: 'family=Rubik:wght@300..900',
  },
  assistant: {
    heading: '"Assistant", system-ui, sans-serif',
    body: '"Assistant", system-ui, sans-serif',
    google: 'family=Assistant:wght@200..800',
  },
  frank: {
    heading: '"Frank Ruhl Libre", Georgia, serif',
    body: '"Assistant", system-ui, sans-serif',
    google: 'family=Frank+Ruhl+Libre:wght@300..900&family=Assistant:wght@200..800',
  },
};

export type StorefrontBlockBorder = {
  enabled: boolean;
  width: StorefrontBorderWidth;
  /** 0–100 */
  opacity: number;
};

/** Focus ring on website controls (independent of admin Theme). */
export type StorefrontFocusOutline = {
  widthPx: number;
  offsetPx: number;
};

export type StorefrontTitleI18n = {
  ru: string;
  en: string;
  he: string;
};

export type StorefrontSlotPresentation = {
  titleI18n: StorefrontTitleI18n;
  showHeading: boolean;
  scale: StorefrontSlotScale;
  /**
   * Vertical padding in px for the section wrapper.
   * null = use site density (`--sf-section-py`).
   */
  paddingYPx: number | null;
  /** Section background role; null = page background (same as shell / «Как у сайта»). */
  bgRole: NoteColorRole | null;
  /** Section text role; null = inherit site text colors. */
  textRole: NoteColorRole | null;
};

export const STOREFRONT_SLOT_PADDING_Y_MIN = 0;
export const STOREFRONT_SLOT_PADDING_Y_MAX = 96;

export type StorefrontAppearance = {
  paletteId: StorefrontPaletteId;
  /** When paletteId is `custom` — build surfaces from this hex + harmony. */
  seedColor: string;
  seedMode: StorefrontSeedMode;
  seedHarmony: StorefrontSeedHarmony;
  fontPairId: StorefrontFontPairId;
  typeScale: StorefrontTypeScale;
  /** Site menu link size, percent of `--sf-font-size-base` (100–250). */
  menuFontScale: number;
  /** Module section titles (h2), percent of base × 1.35 (100–500). */
  moduleHeadingScale: number;
  /** Site menu font-weight (100–900). */
  menuFontWeight: number;
  /** Module section title font-weight (100–900). */
  moduleHeadingWeight: number;
  align: StorefrontAlign;
  density: StorefrontDensity;
  radius: StorefrontRadius;
  contentWidth: StorefrontContentWidth;
  blockBorder: StorefrontBlockBorder;
  /** Focus outline on website inputs/buttons (not admin cabinet). */
  focusOutline: StorefrontFocusOutline;
  /** Footer body text size as % of tenant `--sf-font-size-base`. */
  footerFontScale: number;
  footerFontWeight: number;
  /** Hairlines under header / hero and above footer. */
  sectionDividers: boolean;
  /** Frosted/solid card under body modules (BIO, about, …) for photo readability. */
  contentPanel: StorefrontContentPanel;
  /** Force light/dark text+chrome when a page photo is set. */
  photoChrome: StorefrontPhotoChrome;
  /** Secondary cart/favorites icon placement (footer always has a column). */
  cartFavPlacement: StorefrontCartFavPlacement;
  /** Full-bleed photo behind brand module in hero (and large body brand). */
  brandHeroImageUrl: string | null;
  pageBackground: StorefrontPageBackground;
  /**
   * Sticky header (menu bar) colors. null = palette `--sf-header-bg` / `--sf-header-fg`.
   * Pick e.g. footerBg to match the footer when «Как у сайта» alone is not enough.
   */
  headerBgRole: NoteColorRole | null;
  headerTextRole: NoteColorRole | null;
  slotPresentation: Partial<Record<StorefrontSlotId, StorefrontSlotPresentation>>;
};

export const DEFAULT_SLOT_PRESENTATION: StorefrontSlotPresentation = {
  titleI18n: { ru: '', en: '', he: '' },
  showHeading: true,
  scale: 'md',
  paddingYPx: null,
  bgRole: null,
  textRole: null,
};

export const DEFAULT_STOREFRONT_APPEARANCE: StorefrontAppearance = {
  paletteId: 'chalk_ink',
  seedColor: '#2563eb',
  seedMode: 'light',
  seedHarmony: 'complementary',
  fontPairId: 'system',
  typeScale: 'md',
  menuFontScale: 100,
  moduleHeadingScale: 100,
  menuFontWeight: 500,
  moduleHeadingWeight: 600,
  align: 'left',
  density: 'comfortable',
  radius: 'soft',
  contentWidth: 'narrow',
  blockBorder: {
    enabled: false,
    width: 1,
    opacity: 40,
  },
  focusOutline: { ...DEFAULT_FOCUS_OUTLINE },
  footerFontScale: 100,
  footerFontWeight: 500,
  sectionDividers: false,
  contentPanel: 'soft',
  photoChrome: 'palette',
  cartFavPlacement: 'header_lang',
  brandHeroImageUrl: null,
  pageBackground: { ...DEFAULT_PAGE_BACKGROUND },
  headerBgRole: null,
  headerTextRole: null,
  slotPresentation: {},
};

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export function isValidAppearanceHex(value: string): boolean {
  return HEX_COLOR.test(value.trim());
}

function clamp01(n: number) {
  return Math.min(1, Math.max(0, n));
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const h = hex.trim();
  if (!HEX_COLOR.test(h)) return null;
  return {
    r: Number.parseInt(h.slice(1, 3), 16),
    g: Number.parseInt(h.slice(3, 5), 16),
    b: Number.parseInt(h.slice(5, 7), 16),
  };
}

function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h: h * 360, s, l };
}

function hue2rgb(p: number, q: number, t: number) {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

function hslToHex(h: number, s: number, l: number): string {
  const hh = ((h % 360) + 360) % 360;
  const ss = clamp01(s);
  const ll = clamp01(l);
  if (ss === 0) {
    const v = Math.round(ll * 255);
    return `#${v.toString(16).padStart(2, '0').repeat(3)}`;
  }
  const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
  const p = 2 * ll - q;
  const hk = hh / 360;
  const r = Math.round(hue2rgb(p, q, hk + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, hk) * 255);
  const b = Math.round(hue2rgb(p, q, hk - 1 / 3) * 255);
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

function wrapHue(h: number): number {
  return ((h % 360) + 360) % 360;
}

/**
 * Hue roles from SmartUI smartcolors schemes (mono / complementary / analogous / triadic).
 * primary = seed; accent & support tint footer / block borders.
 */
function huesForHarmony(
  seedHue: number,
  harmony: StorefrontSeedHarmony,
): { primary: number; accent: number; support: number } {
  const h = wrapHue(seedHue);
  switch (harmony) {
    case 'mono':
      return { primary: h, accent: h, support: h };
    case 'analogous':
      return { primary: h, accent: wrapHue(h + 30), support: wrapHue(h - 30) };
    case 'triadic':
      return { primary: h, accent: wrapHue(h + 120), support: wrapHue(h + 240) };
    case 'complementary':
    default:
      return { primary: h, accent: wrapHue(h + 180), support: wrapHue(h + 180) };
  }
}

/**
 * Build site surfaces from one seed color + SmartUI-style harmony.
 * Primary hue for page/header/text; accent/support for footer + block borders.
 */
export function generatePaletteFromSeed(
  seedHex: string,
  mode: StorefrontSeedMode,
  harmony: StorefrontSeedHarmony = 'complementary',
): StorefrontPaletteTokens {
  const rgb = hexToRgb(seedHex) || hexToRgb('#2563eb')!;
  const { h, s } = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const sat = Math.min(s, 0.55);
  const { primary, accent, support } = huesForHarmony(h, harmony);

  if (mode === 'dark') {
    return {
      pageBg: hslToHex(primary, sat * 0.35, 0.09),
      headerBg: hslToHex(primary, sat * 0.4, 0.13),
      footerBg: hslToHex(support, sat * 0.28, 0.07),
      textPrimary: hslToHex(primary, sat * 0.12, 0.93),
      textSecondary: hslToHex(primary, sat * 0.18, 0.7),
      borderColor: hslToHex(primary, sat * 0.22, 0.22),
      blockBorderColor: hslToHex(accent, sat * 0.35, 0.42),
    };
  }

  return {
    pageBg: hslToHex(primary, sat * 0.22, 0.97),
    headerBg: hslToHex(primary, sat * 0.28, 0.94),
    footerBg: hslToHex(support, sat * 0.18, 0.93),
    textPrimary: hslToHex(primary, sat * 0.45, 0.14),
    textSecondary: hslToHex(primary, sat * 0.28, 0.38),
    borderColor: hslToHex(primary, sat * 0.18, 0.86),
    blockBorderColor: hslToHex(accent, sat * 0.32, 0.62),
  };
}

export function resolveAppearancePalette(appearance: StorefrontAppearance): StorefrontPaletteTokens {
  if (appearance.paletteId === 'custom') {
    const seed = isValidAppearanceHex(appearance.seedColor)
      ? appearance.seedColor.trim()
      : DEFAULT_STOREFRONT_APPEARANCE.seedColor;
    return generatePaletteFromSeed(seed, appearance.seedMode, appearance.seedHarmony);
  }
  return STOREFRONT_PALETTES[appearance.paletteId] || STOREFRONT_PALETTES.chalk_ink;
}

export function isDarkAppearancePalette(appearance: StorefrontAppearance): boolean {
  if (appearance.paletteId === 'custom') return appearance.seedMode === 'dark';
  return (
    appearance.paletteId === 'midnight'
    || appearance.paletteId === 'charcoal_sand'
    || appearance.paletteId === 'deep_forest'
    || appearance.paletteId === 'ink_violet'
  );
}

function asRecord(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
}

function normalizeTitleI18n(raw: unknown): StorefrontTitleI18n {
  const source = asRecord(raw);
  return {
    ru: String(source.ru ?? '').trim(),
    en: String(source.en ?? '').trim(),
    he: String(source.he ?? '').trim(),
  };
}

function normalizeSlotColorRole(raw: unknown): NoteColorRole | null {
  const id = String(raw || '').trim();
  if (!id || id === 'default' || id === 'none' || id === 'inherit') return null;
  return (NOTE_COLOR_ROLES as readonly string[]).includes(id)
    ? (id as NoteColorRole)
    : null;
}

function normalizeSlotPaddingYPx(raw: unknown): number | null {
  if (raw == null || raw === '' || raw === 'auto' || raw === 'default') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(
    STOREFRONT_SLOT_PADDING_Y_MAX,
    Math.max(STOREFRONT_SLOT_PADDING_Y_MIN, Math.round(n)),
  );
}

export function normalizeSlotPresentation(raw: unknown): StorefrontSlotPresentation {
  const source = asRecord(raw);
  const scaleRaw = String(source.scale || '').trim().toLowerCase();
  const scale = STOREFRONT_SLOT_SCALES.includes(scaleRaw as StorefrontSlotScale)
    ? (scaleRaw as StorefrontSlotScale)
    : 'md';
  return {
    titleI18n: normalizeTitleI18n(source.titleI18n ?? source.title_i18n),
    showHeading: source.showHeading !== false && source.show_heading !== false,
    scale,
    paddingYPx: normalizeSlotPaddingYPx(
      source.paddingYPx ?? source.padding_y_px,
    ),
    bgRole: normalizeSlotColorRole(source.bgRole ?? source.bg_role),
    textRole: normalizeSlotColorRole(source.textRole ?? source.text_role),
  };
}

function normalizeSlotPresentationMap(
  raw: unknown,
): Partial<Record<StorefrontSlotId, StorefrontSlotPresentation>> {
  const source = asRecord(raw);
  const out: Partial<Record<StorefrontSlotId, StorefrontSlotPresentation>> = {};
  for (const slot of ALL_SLOTS) {
    if (source[slot] == null) continue;
    out[slot] = normalizeSlotPresentation(source[slot]);
  }
  return out;
}

export function normalizeStorefrontAppearance(
  raw: unknown,
  legacyContentWidth?: unknown,
): StorefrontAppearance {
  const source = asRecord(raw);
  const paletteRaw = String(source.paletteId ?? source.palette_id ?? '').trim();
  const paletteId = STOREFRONT_PALETTE_IDS.includes(paletteRaw as StorefrontPaletteId)
    ? (paletteRaw as StorefrontPaletteId)
    : DEFAULT_STOREFRONT_APPEARANCE.paletteId;

  const fontRaw = String(source.fontPairId ?? source.font_pair_id ?? '').trim();
  const fontPairId = STOREFRONT_FONT_PAIR_IDS.includes(fontRaw as StorefrontFontPairId)
    ? (fontRaw as StorefrontFontPairId)
    : DEFAULT_STOREFRONT_APPEARANCE.fontPairId;

  const typeRaw = String(source.typeScale ?? source.type_scale ?? '').trim().toLowerCase();
  const typeScale = STOREFRONT_TYPE_SCALES.includes(typeRaw as StorefrontTypeScale)
    ? (typeRaw as StorefrontTypeScale)
    : DEFAULT_STOREFRONT_APPEARANCE.typeScale;
  const menuFontScale = normalizeAppearancePercentScale(
    source.menuFontScale ?? source.menu_font_scale,
    DEFAULT_STOREFRONT_APPEARANCE.menuFontScale,
    STOREFRONT_MENU_FONT_SCALE_MIN,
    STOREFRONT_MENU_FONT_SCALE_MAX,
  );
  const moduleHeadingScale = normalizeAppearancePercentScale(
    source.moduleHeadingScale ?? source.module_heading_scale,
    DEFAULT_STOREFRONT_APPEARANCE.moduleHeadingScale,
    STOREFRONT_MODULE_HEADING_SCALE_MIN,
    STOREFRONT_MODULE_HEADING_SCALE_MAX,
  );
  const menuFontWeight = normalizeAppearanceFontWeight(
    source.menuFontWeight ?? source.menu_font_weight,
    DEFAULT_STOREFRONT_APPEARANCE.menuFontWeight,
  );
  const moduleHeadingWeight = normalizeAppearanceFontWeight(
    source.moduleHeadingWeight ?? source.module_heading_weight,
    DEFAULT_STOREFRONT_APPEARANCE.moduleHeadingWeight,
  );

  const alignRaw = String(source.align ?? '').trim().toLowerCase();
  const align = STOREFRONT_ALIGNS.includes(alignRaw as StorefrontAlign)
    ? (alignRaw as StorefrontAlign)
    : DEFAULT_STOREFRONT_APPEARANCE.align;

  const densityRaw = String(source.density ?? '').trim().toLowerCase();
  const density = STOREFRONT_DENSITIES.includes(densityRaw as StorefrontDensity)
    ? (densityRaw as StorefrontDensity)
    : DEFAULT_STOREFRONT_APPEARANCE.density;

  const radiusRaw = String(source.radius ?? '').trim().toLowerCase();
  const radius = STOREFRONT_RADII.includes(radiusRaw as StorefrontRadius)
    ? (radiusRaw as StorefrontRadius)
    : DEFAULT_STOREFRONT_APPEARANCE.radius;

  const widthRaw = String(
    source.contentWidth ?? source.content_width ?? legacyContentWidth ?? '',
  ).trim().toLowerCase();
  const contentWidth: StorefrontContentWidth = widthRaw === 'full' ? 'full' : 'narrow';

  const borderRaw = asRecord(source.blockBorder ?? source.block_border);
  const widthNum = Number(borderRaw.width);
  const borderWidth = STOREFRONT_BORDER_WIDTHS.includes(widthNum as StorefrontBorderWidth)
    ? (widthNum as StorefrontBorderWidth)
    : 1;
  let opacity = Number(borderRaw.opacity);
  if (!Number.isFinite(opacity)) opacity = DEFAULT_STOREFRONT_APPEARANCE.blockBorder.opacity;
  opacity = Math.min(100, Math.max(0, Math.round(opacity)));

  const focusOutline = normalizeFocusOutline(
    source.focusOutline ?? source.focus_outline,
  );
  const footerFontScale = normalizeAppearancePercentScale(
    source.footerFontScale ?? source.footer_font_scale,
    DEFAULT_STOREFRONT_APPEARANCE.footerFontScale,
    50,
    250,
  );
  const footerFontWeight = normalizeAppearanceFontWeight(
    source.footerFontWeight ?? source.footer_font_weight,
    DEFAULT_STOREFRONT_APPEARANCE.footerFontWeight,
  );

  const heroUrl = String(
    source.brandHeroImageUrl ?? source.brand_hero_image_url ?? '',
  ).trim();

  const seedRaw = String(source.seedColor ?? source.seed_color ?? '').trim();
  const seedColor = isValidAppearanceHex(seedRaw)
    ? seedRaw
    : DEFAULT_STOREFRONT_APPEARANCE.seedColor;
  const seedModeRaw = String(source.seedMode ?? source.seed_mode ?? '').trim().toLowerCase();
  const seedMode: StorefrontSeedMode = seedModeRaw === 'dark' ? 'dark' : 'light';
  const harmonyRaw = String(source.seedHarmony ?? source.seed_harmony ?? '')
    .trim()
    .toLowerCase();
  const seedHarmony: StorefrontSeedHarmony = STOREFRONT_SEED_HARMONIES.includes(
    harmonyRaw as StorefrontSeedHarmony,
  )
    ? (harmonyRaw as StorefrontSeedHarmony)
    : DEFAULT_STOREFRONT_APPEARANCE.seedHarmony;

  const pageBgRaw = asRecord(source.pageBackground ?? source.page_background);
  const pageModeRaw = String(pageBgRaw.mode || '').trim().toLowerCase();
  const pageMode: StorefrontPageBgMode = pageModeRaw === 'pattern' ? 'pattern' : 'cover';
  let patternSize = Number(pageBgRaw.patternSize ?? pageBgRaw.pattern_size);
  if (!Number.isFinite(patternSize)) patternSize = DEFAULT_PAGE_BACKGROUND.patternSize;
  patternSize = Math.min(640, Math.max(64, Math.round(patternSize)));
  const overlayBalance = normalizeOverlayBalance(pageBgRaw);
  const pageImage = String(pageBgRaw.imageUrl ?? pageBgRaw.image_url ?? '').trim();

  const panelRaw = String(source.contentPanel ?? source.content_panel ?? '')
    .trim()
    .toLowerCase();
  const contentPanel: StorefrontContentPanel = STOREFRONT_CONTENT_PANELS.includes(
    panelRaw as StorefrontContentPanel,
  )
    ? (panelRaw as StorefrontContentPanel)
    : DEFAULT_STOREFRONT_APPEARANCE.contentPanel;

  const chromeRaw = String(source.photoChrome ?? source.photo_chrome ?? '')
    .trim()
    .toLowerCase();
  const photoChrome: StorefrontPhotoChrome = STOREFRONT_PHOTO_CHROMES.includes(
    chromeRaw as StorefrontPhotoChrome,
  )
    ? (chromeRaw as StorefrontPhotoChrome)
    : DEFAULT_STOREFRONT_APPEARANCE.photoChrome;

  const placementRaw = String(source.cartFavPlacement ?? source.cart_fav_placement ?? '')
    .trim()
    .toLowerCase();
  const cartFavPlacement: StorefrontCartFavPlacement = STOREFRONT_CART_FAV_PLACEMENTS.includes(
    placementRaw as StorefrontCartFavPlacement,
  )
    ? (placementRaw as StorefrontCartFavPlacement)
    : DEFAULT_STOREFRONT_APPEARANCE.cartFavPlacement;

  return {
    paletteId,
    seedColor,
    seedMode,
    seedHarmony,
    fontPairId,
    typeScale,
    menuFontScale,
    moduleHeadingScale,
    menuFontWeight,
    moduleHeadingWeight,
    align,
    density,
    radius,
    contentWidth,
    blockBorder: {
      enabled: borderRaw.enabled === true || borderRaw.enabled === 'true',
      width: borderWidth,
      opacity,
    },
    focusOutline,
    footerFontScale,
    footerFontWeight,
    sectionDividers:
      source.sectionDividers === true
      || source.section_dividers === true,
    contentPanel,
    photoChrome,
    cartFavPlacement,
    brandHeroImageUrl: heroUrl || null,
    pageBackground: {
      imageUrl: pageImage || null,
      mode: pageMode,
      patternSize,
      overlayBalance,
    },
    headerBgRole: normalizeSlotColorRole(
      source.headerBgRole ?? source.header_bg_role,
    ),
    headerTextRole: normalizeSlotColorRole(
      source.headerTextRole ?? source.header_text_role,
    ),
    slotPresentation: normalizeSlotPresentationMap(
      source.slotPresentation ?? source.slot_presentation,
    ),
  };
}

export function getSlotPresentation(
  appearance: StorefrontAppearance,
  slot: StorefrontSlotId,
): StorefrontSlotPresentation {
  return appearance.slotPresentation[slot]
    ? { ...DEFAULT_SLOT_PRESENTATION, ...appearance.slotPresentation[slot] }
    : { ...DEFAULT_SLOT_PRESENTATION };
}

export function findSlotForModule(
  bindings: StorefrontBindings,
  moduleId: StorefrontModuleId,
): StorefrontSlotId | null {
  for (const slot of ALL_SLOTS) {
    if (bindings[slot] === moduleId) return slot;
  }
  return null;
}

/** Connected body slots (hero/main/footer) with a real module — for Appearance UI. */
export function listPresentableBodySlots(
  bindings: StorefrontBindings,
): Array<{ slot: StorefrontSlotId; moduleId: StorefrontModuleId }> {
  const out: Array<{ slot: StorefrontSlotId; moduleId: StorefrontModuleId }> = [];
  for (const slot of BODY_SLOTS) {
    const moduleId = bindings[slot];
    if (!moduleId || moduleId === 'none') continue;
    out.push({ slot, moduleId });
  }
  return out;
}

export function resolveSlotTitle(
  appearance: StorefrontAppearance,
  slot: StorefrontSlotId,
  lang: Language,
  fallback: string,
): string {
  const custom = pickI18nText(getSlotPresentation(appearance, slot).titleI18n, lang);
  return custom || fallback;
}

/**
 * Inline styles for a body section (main / footer): padding, bg, text.
 * `densityPad` is the CSS value for site-wide vertical padding (e.g. var(--sf-section-py)).
 */
export function resolveSlotSectionStyle(
  appearance: StorefrontAppearance,
  slot: StorefrontSlotId,
  densityPad: string,
  options?: { defaultBackground?: string },
): Record<string, string> {
  const pres = getSlotPresentation(appearance, slot);
  const style: Record<string, string> = {
    paddingBlock: pres.paddingYPx != null ? `${pres.paddingYPx}px` : densityPad,
  };
  if (pres.bgRole) {
    style.background = noteColorRoleCssVar(pres.bgRole);
  } else if (options?.defaultBackground) {
    style.background = options.defaultBackground;
  }
  if (pres.textRole) {
    const text = noteColorRoleCssVar(pres.textRole);
    style.color = text;
    style['--text-primary'] = text;
  }
  return style;
}

/**
 * Sticky website header chrome. Overrides palette `--sf-header-*` when roles set.
 */
export function resolveHeaderChromeStyle(
  appearance: StorefrontAppearance,
): Record<string, string> {
  const style: Record<string, string> = {
    background: 'var(--sf-header-bg, var(--bg-primary))',
    color: 'var(--sf-header-fg, var(--text-primary))',
  };
  if (appearance.headerBgRole) {
    const bg = noteColorRoleCssVar(appearance.headerBgRole);
    style.background = bg;
    style['--sf-header-bg'] = bg;
  }
  if (appearance.headerTextRole) {
    const fg = noteColorRoleCssVar(appearance.headerTextRole);
    style.color = fg;
    style['--sf-header-fg'] = fg;
  }
  return style;
}

export function resolveModuleTitle(
  appearance: StorefrontAppearance,
  bindings: StorefrontBindings,
  moduleId: StorefrontModuleId,
  lang: Language,
  fallback: string,
): string {
  const slot = findSlotForModule(bindings, moduleId);
  if (!slot) return fallback;
  return resolveSlotTitle(appearance, slot, lang, fallback);
}

const TYPE_SCALE_CSS: Record<StorefrontTypeScale, string> = {
  sm: '0.92',
  md: '1',
  lg: '1.12',
};

const DENSITY_PY: Record<StorefrontDensity, string> = {
  compact: '1.25rem',
  comfortable: '1.75rem',
};

const RADIUS_CSS: Record<StorefrontRadius, { control: string; surface: string }> = {
  sharp: { control: '0.25rem', surface: '0.35rem' },
  soft: { control: '0.75rem', surface: '1rem' },
  round: { control: '1.25rem', surface: '1.5rem' },
};

const SLOT_SCALE_CSS: Record<StorefrontSlotScale, string> = {
  sm: '0.9',
  md: '1',
  lg: '1.2',
  xl: '1.45',
};

export function slotScaleFactor(scale: StorefrontSlotScale): string {
  return SLOT_SCALE_CSS[scale];
}

const GOOGLE_LINK_ID = 'catalogofworks-storefront-fonts';

function ensureGoogleFont(query: string | undefined) {
  if (!query || typeof document === 'undefined') return;
  let link = document.getElementById(GOOGLE_LINK_ID) as HTMLLinkElement | null;
  const href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  if (!link) {
    link = document.createElement('link');
    link.id = GOOGLE_LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

/** Appearance tokens only (safe for dialog portal hosts — no page background paint). */
export function applyStorefrontAppearanceTokens(
  el: HTMLElement | null,
  appearance: StorefrontAppearance,
) {
  if (!el) return;
  const palette = resolveAppearancePalette(appearance);
  const fonts = STOREFRONT_FONT_PAIRS[appearance.fontPairId] || STOREFRONT_FONT_PAIRS.system;
  ensureGoogleFont(fonts.google);

  const radius = RADIUS_CSS[appearance.radius];
  const border = appearance.blockBorder;
  const borderAlpha = Math.min(1, Math.max(0, border.opacity / 100));
  const pageBg = appearance.pageBackground;
  const hasPhoto = Boolean(pageBg.imageUrl);
  const photoChrome = hasPhoto ? appearance.photoChrome : 'palette';
  const forceLight = photoChrome === 'light';
  const forceDark = photoChrome === 'dark';
  const darkChrome =
    forceLight
    || (!forceDark && (appearance.paletteId === 'graphite' || isDarkAppearancePalette(appearance)));

  const textPrimary = forceLight
    ? '#f8fafc'
    : forceDark
      ? '#0f172a'
      : palette.textPrimary;
  const textSecondary = forceLight
    ? '#cbd5e1'
    : forceDark
      ? '#475569'
      : palette.textSecondary;
  const headerBg = forceLight
    ? 'rgba(15, 23, 42, 0.92)'
    : forceDark
      ? '#ffffff'
      : palette.headerBg;
  const footerBg = forceLight
    ? 'rgba(15, 23, 42, 0.92)'
    : forceDark
      ? '#f8fafc'
      : palette.footerBg;
  const borderColor = forceLight
    ? '#334155'
    : forceDark
      ? '#e2e8f0'
      : palette.borderColor;

  let panelBg = 'transparent';
  let panelBlur = 'none';
  if (appearance.contentPanel === 'solid') {
    panelBg = forceLight ? 'rgba(15, 23, 42, 0.94)' : palette.pageBg;
    panelBlur = 'none';
  } else if (appearance.contentPanel === 'soft') {
    // Frosted wash only helps over a page photo. On a flat page color the old
    // 86% mix + blur reads as a different shade than solid footer/page bg.
    if (hasPhoto) {
      panelBg = forceLight
        ? 'rgba(15, 23, 42, 0.72)'
        : `color-mix(in srgb, ${palette.pageBg} 86%, transparent)`;
      panelBlur = 'blur(12px)';
    } else {
      panelBg = forceLight ? 'rgba(15, 23, 42, 0.94)' : palette.pageBg;
      panelBlur = 'none';
    }
  }

  el.style.setProperty('--bg-primary', forceLight ? '#0f172a' : palette.pageBg);
  el.style.setProperty('--bg-secondary', headerBg);
  el.style.setProperty('--sf-page-bg', palette.pageBg);
  el.style.setProperty('--sf-header-bg', headerBg);
  el.style.setProperty('--sf-header-fg', darkChrome ? '#f8fafc' : textPrimary);
  el.style.setProperty('--sf-footer-bg', footerBg);
  el.style.setProperty('--text-primary', textPrimary);
  el.style.setProperty('--text-secondary', textSecondary);
  el.style.setProperty('--border-color', borderColor);
  el.style.setProperty('--sf-content-panel-bg', panelBg);
  el.style.setProperty('--sf-content-panel-blur', panelBlur);
  el.style.setProperty('--sf-type-scale', TYPE_SCALE_CSS[appearance.typeScale]);
  el.style.setProperty(
    '--sf-menu-font-size',
    `calc(var(--sf-font-size-base, 1rem) * ${appearance.menuFontScale / 100})`,
  );
  el.style.setProperty(
    '--sf-module-heading-size',
    `calc(var(--sf-font-size-base, 1rem) * 1.35 * ${appearance.moduleHeadingScale / 100})`,
  );
  el.style.setProperty('--sf-menu-font-weight', String(appearance.menuFontWeight));
  el.style.setProperty('--sf-module-heading-weight', String(appearance.moduleHeadingWeight));
  el.style.setProperty(
    '--sf-footer-font-size',
    `calc(var(--sf-font-size-base, 1rem) * ${appearance.footerFontScale / 100})`,
  );
  el.style.setProperty('--sf-footer-font-weight', String(appearance.footerFontWeight));
  el.style.setProperty('--sf-section-py', DENSITY_PY[appearance.density]);
  el.style.setProperty('--radius-control', radius.control);
  el.style.setProperty('--radius-surface', radius.surface);
  el.style.setProperty('--sf-font-heading', fonts.heading);
  el.style.setProperty('--sf-font-body', fonts.body);
  el.style.setProperty('--sf-align', appearance.align);
  el.style.setProperty(
    '--sf-block-border',
    border.enabled
      ? `${border.width}px solid color-mix(in srgb, ${palette.blockBorderColor} ${Math.round(borderAlpha * 100)}%, transparent)`
      : 'none',
  );
  const focusCss = focusOutlineCssVars(appearance.focusOutline);
  el.style.setProperty('--focus-outline-width', focusCss.width);
  el.style.setProperty('--focus-outline-offset', focusCss.offset);
  el.dataset.sfContentPanel = appearance.contentPanel;
  el.dataset.sfPhotoChrome = photoChrome;
  el.style.fontFamily = fonts.body;
  // Base size comes from Theme (`--sf-font-size-base` on documentElement).
  el.style.fontSize = 'var(--sf-font-size-base, 1rem)';
  el.style.color = textPrimary;
}

/** Apply appearance CSS variables + page surface on website shell root. */
export function applyStorefrontAppearanceCss(
  el: HTMLElement | null,
  appearance: StorefrontAppearance,
) {
  if (!el) return;
  applyStorefrontAppearanceTokens(el, appearance);

  const palette = resolveAppearancePalette(appearance);
  const pageBg = appearance.pageBackground;

  if (pageBg.imageUrl) {
    const veil = pageBgOverlayVeil(pageBg.overlayBalance);
    const safeUrl = pageBg.imageUrl.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    el.style.backgroundImage = veil
      ? `${veil}, url("${safeUrl}")`
      : `url("${safeUrl}")`;
    if (pageBg.mode === 'pattern') {
      el.style.backgroundSize = `${pageBg.patternSize}px ${pageBg.patternSize}px`;
      el.style.backgroundRepeat = 'repeat';
      el.style.backgroundAttachment = 'scroll';
    } else {
      el.style.backgroundSize = 'cover';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.backgroundPosition = 'center';
      el.style.backgroundAttachment = 'fixed';
    }
  } else {
    el.style.backgroundImage = '';
    el.style.backgroundSize = '';
    el.style.backgroundRepeat = '';
    el.style.backgroundPosition = '';
    el.style.backgroundAttachment = '';
    el.style.backgroundColor = palette.pageBg;
  }
}

/** Inline styles for body module cards (BIO, about, …) — padding via className. */
export function contentPanelWrapStyle(
  appearance: StorefrontAppearance,
): Record<string, string> | undefined {
  if (appearance.contentPanel === 'off') return undefined;
  return {
    background: 'var(--sf-content-panel-bg)',
    backdropFilter: 'var(--sf-content-panel-blur)',
    WebkitBackdropFilter: 'var(--sf-content-panel-blur)',
    borderRadius: 'var(--radius-surface)',
  };
}

/** Responsive panel padding: edge-to-edge on mobile, 1.25rem sides from sm. */
export function contentPanelWrapClassName(
  appearance: StorefrontAppearance,
): string {
  if (appearance.contentPanel === 'off') return '';
  return 'px-0 py-4 sm:px-5';
}

export const STOREFRONT_PALETTE_LABEL_KEYS: Record<StorefrontPaletteId, string> = {
  chalk_ink: 'settings.appearancePalette_chalk_ink',
  mist_navy: 'settings.appearancePalette_mist_navy',
  forest_fog: 'settings.appearancePalette_forest_fog',
  sand_espresso: 'settings.appearancePalette_sand_espresso',
  ocean_slate: 'settings.appearancePalette_ocean_slate',
  rose_stone: 'settings.appearancePalette_rose_stone',
  graphite: 'settings.appearancePalette_graphite',
  lilac_ink: 'settings.appearancePalette_lilac_ink',
  midnight: 'settings.appearancePalette_midnight',
  charcoal_sand: 'settings.appearancePalette_charcoal_sand',
  deep_forest: 'settings.appearancePalette_deep_forest',
  ink_violet: 'settings.appearancePalette_ink_violet',
  custom: 'settings.appearancePalette_custom',
};

export const STOREFRONT_FONT_PAIR_LABEL_KEYS: Record<StorefrontFontPairId, string> = {
  system: 'settings.appearanceFont_system',
  classic: 'settings.appearanceFont_classic',
  modern: 'settings.appearanceFont_modern',
  editorial: 'settings.appearanceFont_editorial',
  heebo: 'settings.appearanceFont_heebo',
  rubik: 'settings.appearanceFont_rubik',
  assistant: 'settings.appearanceFont_assistant',
  frank: 'settings.appearanceFont_frank',
};
